import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Box,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  LinearProgress,
  Typography,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuth } from "./auth";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";


const API_BASE = process.env.REACT_APP_API_BASE || "";

const buildColumnVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.id] = true;
    return acc;
  }, {});

const buildSummonVisibility = () => buildColumnVisibility(SUMMON_COLUMNS);

const buildColumnWidths = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.id] = col.minWidth || 120;
    return acc;
  }, {});

const SUMMON_COLUMNS = [
  { id: "srno", label: "SrNo", minWidth: 80 },
  { id: "enquirycasenum", label: "Enquiry Case Number", minWidth: 180 },
  { id: "priority", label: "Priority", minWidth: 120 },
  { id: "cname", label: "Name", minWidth: 140 },
  { id: "address", label: "Address", minWidth: 220, wrap: true },
  { id: "phone", label: "Phone", minWidth: 140 },
  { id: "cnic", label: "CNIC", minWidth: 150 },
  { id: "complainantname", label: "Complainant Name", minWidth: 180 },
  { id: "gistofallegations", label: "Gist of Allegations", minWidth: 260, wrap: true },
  { id: "roleofalleged", label: "Role of Alleged", minWidth: 180 },
  { id: "attendon", label: "AttendOn", minWidth: 140 },
  { id: "noticeprogress", label: "Notice Progress", minWidth: 220 },
  { id: "notice1dispatchdate", label: "1st Notice", minWidth: 140 },
  { id: "notice1appearancedate", label: "1st Appear", minWidth: 140 },
  { id: "notice2dispatchdate", label: "2nd Notice", minWidth: 140 },
  { id: "notice2appearancedate", label: "2nd Appear", minWidth: 140 },
  { id: "notice3dispatchdate", label: "3rd Notice", minWidth: 140 },
  { id: "notice3appearancedate", label: "3rd Appear", minWidth: 140 },
  { id: "dpoletterdispatchdate", label: "DPO Dispatch", minWidth: 160 },
  { id: "dpoletterappearancedate", label: "DPO Status", minWidth: 160 },
  { id: "actions", label: "Actions", minWidth: 160 },
];

const SUMMON_COLUMN_MAP = SUMMON_COLUMNS.reduce((acc, col) => {
  acc[col.id] = col;
  return acc;
}, {});

const getPriorityChipColor = (priority) => {
  switch (String(priority || "").toLowerCase()) {
    case "high":
      return "error";
    case "medium":
      return "warning";
    case "low":
      return "success";
    default:
      return "default";
  }
};

const normalizeKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const excelDateToISO = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().slice(0, 10);
  }
  return "";
};

const parseDateTokenWithError = (raw) => {
  if (!raw) return { iso: "", error: "Invalid date format. Use DD/MM/YYY or MM/DD/YYY." };
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return { iso: raw, error: "" };

  const simpleMatch = raw.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})$/);
  if (simpleMatch) {
    let [, p1, p2, y] = simpleMatch;
    if (y.length === 2) y = `20${y}`;
    if (y.length === 3) y = `20${y}`;
    let d = p1;
    let m = p2;
    if (Number(p1) > 12) {
      d = p1;
      m = p2;
    } else if (Number(p2) > 12) {
      d = p2;
      m = p1;
    } else {
      // ambiguous; default to month/day/year
      d = p2;
      m = p1;
    }
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const iso = `${y}-${mm}-${dd}`;
    const check = new Date(iso);
    return Number.isNaN(check.getTime())
      ? { iso: "", error: "Invalid date value" }
      : { iso, error: "" };
  }

  return { iso: "", error: "Invalid date format. Use DD/MM/YYY or MM/DD/YYY." };
};

const parseDateWithError = (value) => {
  if (value === null || value === undefined || value === "") {
    return { iso: "", error: "" };
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { iso: value.toISOString().slice(0, 10), error: "" };
  }
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return Number.isNaN(date.getTime())
      ? { iso: "", error: "Invalid Excel date number" }
      : { iso: date.toISOString().slice(0, 10), error: "" };
  }
  const raw = String(value).trim();
  if (!raw) return { iso: "", error: "" };
  const direct = parseDateTokenWithError(raw);
  if (direct.iso) return direct;

  const tokens = raw.match(/\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4}/g) || [];
  for (const token of tokens) {
    const parsed = parseDateTokenWithError(token);
    if (parsed.iso) return parsed;
  }

  return direct;
};

const splitSerialList = (value) => {
  if (!value) return [];
  const raw = String(value).trim();
  if (!raw) return [];
  let parts = raw.split(/\r?\n|;|•/).map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) {
    parts = raw
      .split(/\s*\d+\s*[\).:\-]\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return parts.length ? parts : [raw];
};

const extractNameCnic = (entry) => {
  const text = String(entry || "").trim();
  const cnicMatch =
    text.match(/\b\d{5}-\d{7}-\d{1}\b/) || text.match(/\b\d{13}\b/);
  const cnic = cnicMatch ? cnicMatch[0] : "";
  const phoneMatch = text.match(/\b03\d{9}\b/) || text.match(/\b\d{11}\b/);
  const phone = phoneMatch ? phoneMatch[0] : "";
  let name = text.replace(cnic, "");
  name = phone ? name.replace(phone, "") : name;
  name = name.replace(/[()\-–—]/g, " ").replace(/\s+/g, " ").trim();
  return { name, cnic, phone };
};

const computeNextNotice = ({
  notice1DispatchDate,
  notice2DispatchDate,
  notice3DispatchDate,
  dpoLetterDispatchDate,
}) => {
  if (dpoLetterDispatchDate) return "DPO Letter";
  if (notice3DispatchDate) return "DPO Letter";
  if (notice2DispatchDate) return "3rd Notice";
  if (notice1DispatchDate) return "2nd Notice";
  return "1st Notice";
};

const getNoticeChipColor = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("1st")) return "info";
  if (v.includes("2nd")) return "warning";
  if (v.includes("3rd")) return "error";
  if (v.includes("dpo")) return "secondary";
  return "default";
};

const IO_DESIGNATION_OPTIONS = [
  { code: "AD", value: "Assistant Director", label: "Assistant Director (AD)" },
  { code: "IP", value: "Inspector", label: "Inspector (IP)" },
  { code: "SI", value: "Sub-Inspector", label: "Sub-Inspector (SI)" },
];

const normalizeIoDesignation = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const upper = raw.toUpperCase();
  if (upper === "AD") return "Assistant Director";
  if (upper === "IP") return "Inspector";
  if (upper === "SI") return "Sub-Inspector";
  const matched = IO_DESIGNATION_OPTIONS.find((option) => option.value === raw);
  return matched?.value || "";
};

const getIoDesignationCode = (value) => {
  const normalized = normalizeIoDesignation(value);
  const matched = IO_DESIGNATION_OPTIONS.find((option) => option.value === normalized);
  return matched?.code || "";
};

const SummonNotice = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canEdit, canDelete, user, refresh } = useAuth();
  const [profileData, setProfileData] = useState({
    investigationOfficerName: "",
    ioDesignation: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    cnic: "",
    noticeNum: "1st Notice",
    enquiryCaseNum: "",
    complainantName: "",
    gistOfAllegations: "",
    roleOfAlleged: "",
    attendOn: "",
    notice1DispatchDate: "",
    notice1AppearanceDate: "",
    notice2DispatchDate: "",
    notice2AppearanceDate: "",
    notice3DispatchDate: "",
    notice3AppearanceDate: "",
    dpoLetterDispatchDate: "",
    dpoLetterAppearanceDate: "",
  });

  
const downloadDocx = async (srno) => {
  const response = await fetch(`${API_BASE}/api/summons/${srno}/docx`);
  if (!response.ok) throw new Error("Failed to download document");
  const blob = await response.blob();
  saveAs(blob, `Summon_${srno}.docx`);
};

  // Ensure that these labels exactly match those in the table data.
  const gistOptions = [
    "Online Fraud",
    "Electronic Fraud",
    "Unauthorized use of identity information",
    "Spoofing",
    "Unauthorized issuance of SIM cards etc.",
    "Offences against dignity of a natural person",
    "Offences against modesty of a natural person",
    "Electronic Forgery",
    "Unauthorized access to information system or data / copying or transmission of data / Interference with information system or data",
    "Unauthorized access to critical infrastructure information system or data / copying or transmission of critical infrastructure",
    "Interference with critical infrastructure information system or data",
    "Child pornography",
    "Unauthorized interception",
    "Tampering, etc. of communication equipment",
    "Glorification of an offence",
    "Cyber Terrorism",
    "Hate Speech",
    "Making, Obtaining, or supplying device for use in offence",
    "Recruitment, funding and planning of terrorism",
    "Malicious Code",
    "Cyber Stalking",
    "Spamming",
  ];

  const [selectedGist, setSelectedGist] = useState([]);
  const [isOtherGist, setIsOtherGist] = useState(false);
  const [otherGistText, setOtherGistText] = useState("");

  const [summonData, setSummonData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const entriesPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("Add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedTableRow, setSelectedTableRow] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [dateField, setDateField] = useState("dispatch");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importErrors, setImportErrors] = useState([]);
  const [importErrorOpen, setImportErrorOpen] = useState(false);
  const [enquiryOptions, setEnquiryOptions] = useState([]);
  const [manualEnquiry, setManualEnquiry] = useState(true);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquiryStatus, setEnquiryStatus] = useState(null);
  const [personNoticeStatus, setPersonNoticeStatus] = useState(null);
  const [complainantLocked, setComplainantLocked] = useState(false);
  const [summonColumnsOpen, setSummonColumnsOpen] = useState(false);
  const [summonColumnVisibility, setSummonColumnVisibility] = useState(() =>
    buildSummonVisibility()
  );
  const [summonColumnWidths, setSummonColumnWidths] = useState(() =>
    buildColumnWidths(SUMMON_COLUMNS)
  );
  const [search, setSearch] = useState("");
  const [noticeStageFilter, setNoticeStageFilter] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  const currentUserOfficerName = String(
    user?.investigationOfficerName || user?.name || ""
  ).trim();
  const currentUserIoDesignation = normalizeIoDesignation(user?.ioDesignation || user?.rank || "");
  const hasOfficerProfile =
    profileData.investigationOfficerName.trim() &&
    normalizeIoDesignation(profileData.ioDesignation);
  const profileDirty =
    profileData.investigationOfficerName.trim() !== currentUserOfficerName ||
    normalizeIoDesignation(profileData.ioDesignation) !== currentUserIoDesignation;

  useEffect(() => {
    const investigationOfficerName = String(
      user?.investigationOfficerName || user?.name || ""
    ).trim();
    const ioDesignation = normalizeIoDesignation(user?.ioDesignation || user?.rank || "");
    setProfileData({ investigationOfficerName, ioDesignation });
  }, [user?.id, user?.investigationOfficerName, user?.name, user?.ioDesignation, user?.rank]);

  const handleProfileFieldChange = (event) => {
    const { name, value } = event.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const saveOfficerProfile = async () => {
    const investigationOfficerName = profileData.investigationOfficerName.trim();
    const ioDesignation = normalizeIoDesignation(profileData.ioDesignation);
    if (!investigationOfficerName) {
      alert("Investigation Officer Name is required.");
      return false;
    }
    if (!ioDesignation) {
      alert("Please select IO Designation (AD, IP, or SI).");
      return false;
    }
    try {
      setProfileSaving(true);
      const response = await fetch(`${API_BASE}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          investigationOfficerName,
          ioDesignation,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to save profile");
      }
      await refresh();
      return true;
    } catch (error) {
      console.error("Failed to save officer profile:", error);
      alert("Unable to save profile details. Please try again.");
      return false;
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => {
    fetchSummonData(currentPage);
  }, [currentPage, noticeStageFilter, exportDateFrom, exportDateTo, dateField, search]);

  useEffect(() => {
    const loadEnquiries = async () => {
      setEnquiryLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/summons/enquiries`);
        if (!res.ok) throw new Error("Failed to load enquiries");
        const list = await res.json();
        setEnquiryOptions(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Failed to load enquiry list:", error);
        setEnquiryOptions([]);
      } finally {
        setEnquiryLoading(false);
      }
    };
    loadEnquiries();
  }, []);

  useEffect(() => {
    if (!openDialog) return;
    const value = formData.enquiryCaseNum.trim();
    const personName = formData.name.trim();
    if (!value) {
      setEnquiryStatus(null);
      setPersonNoticeStatus(null);
      setComplainantLocked(false);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const statusRes = await fetch(
          `${API_BASE}/api/summons/enquiry-status?case=${encodeURIComponent(value)}${
            personName ? `&name=${encodeURIComponent(personName)}` : ""
          }`
        );
        if (statusRes.ok) {
          const data = await statusRes.json();
          setEnquiryStatus(data);
          setPersonNoticeStatus(data?.person || null);
          const nextNotice = data?.person?.nextNotice || data?.nextNotice;
          if (dialogType === "Add" && nextNotice) {
            setFormData((prev) => ({ ...prev, noticeNum: nextNotice }));
          }
        }

        const complainantRes = await fetch(
          `${API_BASE}/api/enquiries/lookup?case=${encodeURIComponent(value)}`
        );
        if (complainantRes.ok) {
          const info = await complainantRes.json();
          if (info?.nameofcomplainant) {
            setFormData((prev) => ({ ...prev, complainantName: info.nameofcomplainant }));
            setComplainantLocked(true);
          } else {
            setComplainantLocked(false);
          }
        } else {
          const fallbackRes = await fetch(
            `${API_BASE}/api/summons/complainant?case=${encodeURIComponent(value)}`
          );
          if (fallbackRes.ok) {
            const info = await fallbackRes.json();
            if (info?.complainantName) {
              setFormData((prev) => ({ ...prev, complainantName: info.complainantName }));
              setComplainantLocked(true);
            } else {
              setComplainantLocked(false);
            }
          } else {
            setComplainantLocked(false);
          }
        }
      } catch (error) {
        console.error("Failed to fetch enquiry status:", error);
        setEnquiryStatus(null);
        setPersonNoticeStatus(null);
        setComplainantLocked(false);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [dialogType, formData.enquiryCaseNum, formData.name, openDialog]);

  const fetchSummonData = async (page) => {
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", entriesPerPage);
      const normalizedSearch = search.trim();
      if (normalizedSearch) params.set("search", normalizedSearch);
      if (noticeStageFilter) params.set("noticeStage", noticeStageFilter);
      if (exportDateFrom) params.set("dateFrom", exportDateFrom);
      if (exportDateTo) params.set("dateTo", exportDateTo);
      if (dateField) params.set("dateField", dateField);
      const response = await fetch(`${API_BASE}/api/summons?${params.toString()}`);
      if (response.ok) {
        const { data, totalEntries } = await response.json();
        setSummonData(data || []);
        setTotalPages(Math.ceil(totalEntries / entriesPerPage));
      } else {
        console.error("Failed to fetch summon data.");
      }
    } catch (error) {
      console.error("Error fetching summon data:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleGistChange = (event) => {
    if (isOtherGist) return;
    const value = event.target.name;
    const isChecked = event.target.checked;
    const newSelectedGist = isChecked
      ? [...selectedGist, value]
      : selectedGist.filter((item) => item !== value);
    setSelectedGist(newSelectedGist);
    setFormData({
      ...formData,
      gistOfAllegations: newSelectedGist.join(", "),
    });
  };

  const handleOtherGistToggle = (event) => {
    const checked = event.target.checked;
    setIsOtherGist(checked);
    if (checked) {
      setSelectedGist([]);
      setFormData((prev) => ({ ...prev, gistOfAllegations: otherGistText.trim() }));
    } else {
      setOtherGistText("");
      setFormData((prev) => ({ ...prev, gistOfAllegations: "" }));
    }
  };

  const handleOtherGistChange = (event) => {
    const text = event.target.value;
    const normalized = text.replace(/\s+/g, " ").trim();
    const words = normalized ? normalized.split(" ") : [];
    if (words.length > 60) return;
    setOtherGistText(text);
    setFormData((prev) => ({ ...prev, gistOfAllegations: normalized }));
  };

  const otherWordCount = otherGistText.trim().split(/\s+/).filter(Boolean).length;
  const otherWordsRemaining = 60 - otherWordCount;

  const requiredErrors = {
    name: !formData.name.trim(),
    address: !formData.address.trim(),
    noticeNum: !formData.noticeNum.trim(),
    enquiryCaseNum: !formData.enquiryCaseNum.trim(),
    complainantName: !formData.complainantName.trim(),
    gistOfAllegations: !formData.gistOfAllegations.trim(),
  };

  const hasRequiredErrors = Object.values(requiredErrors).some(Boolean);

  const noticeHistoryTooltip = enquiryStatus ? (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" display="block">
        1st Notice: {enquiryStatus.hasNotice1 ? "Sent" : "Not sent"}
      </Typography>
      <Typography variant="caption" display="block">
        2nd Notice: {enquiryStatus.hasNotice2 ? "Sent" : "Not sent"}
      </Typography>
      <Typography variant="caption" display="block">
        3rd Notice: {enquiryStatus.hasNotice3 ? "Sent" : "Not sent"}
      </Typography>
      <Typography variant="caption" display="block">
        DPO Letter: {enquiryStatus.hasDpo ? "Sent" : "Not sent"}
      </Typography>
      {enquiryStatus.nextNotice && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 700 }}>
          Next: {enquiryStatus.nextNotice}
        </Typography>
      )}
    </Box>
  ) : (
    "No enquiry history found"
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const formatInputDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleOpenDialog = (type, row = null) => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    setDialogType(type);
    setSubmitAttempted(false);
    if (type === "Edit" && row) {
      const gistArray = row.gistofallegations ? row.gistofallegations.split(", ") : [];
      const unknownGist = gistArray.filter((g) => !gistOptions.includes(g));
      const computedNotice = row.noticenum || computeNextNotice({
        notice1DispatchDate: row.notice1dispatchdate,
        notice2DispatchDate: row.notice2dispatchdate,
        notice3DispatchDate: row.notice3dispatchdate,
        dpoLetterDispatchDate: row.dpoletterdispatchdate,
      });
      if (unknownGist.length) {
        setIsOtherGist(true);
        setOtherGistText(unknownGist.join(", "));
        setSelectedGist([]);
      } else {
        setIsOtherGist(false);
        setOtherGistText("");
        setSelectedGist(gistArray);
      }
      setFormData({
        name: row.cname || "",
        address: row.address || "",
        phone: row.phone || "",
        cnic: row.cnic || "",
        noticeNum: computedNotice,
        enquiryCaseNum: row.enquirycasenum || "",
        complainantName: row.complainantname || "",
        gistOfAllegations: unknownGist.length ? unknownGist.join(", ") : gistArray.join(", "),
        roleOfAlleged: row.roleofalleged || "",
        attendOn: row.attendon || "",
        notice1DispatchDate: formatDate(row.notice1dispatchdate) || "",
        notice1AppearanceDate: formatDate(row.notice1appearancedate) || "",
        notice2DispatchDate: formatDate(row.notice2dispatchdate) || "",
        notice2AppearanceDate: formatDate(row.notice2appearancedate) || "",
        notice3DispatchDate: formatDate(row.notice3dispatchdate) || "",
        notice3AppearanceDate: formatDate(row.notice3appearancedate) || "",
        dpoLetterDispatchDate: formatDate(row.dpoletterdispatchdate) || "",
        dpoLetterAppearanceDate: formatDate(row.dpoletterappearancedate) || "",
      });
      setEnquiryStatus({
        nextNotice: computedNotice,
        hasNotice1: !!row.notice1dispatchdate,
        hasNotice2: !!row.notice2dispatchdate,
        hasNotice3: !!row.notice3dispatchdate,
        hasDpo: !!row.dpoletterdispatchdate,
      });
      setPersonNoticeStatus(null);
      setComplainantLocked(!!row.complainantname);
      setSelectedRow(row);
    } else {
      setFormData({
        name: "",
        address: "",
        phone: "",
        cnic: "",
        noticeNum: "1st Notice",
        enquiryCaseNum: "",
        complainantName: "",
        gistOfAllegations: "",
        roleOfAlleged: "",
        attendOn: "",
        notice1DispatchDate: "",
        notice1AppearanceDate: "",
        notice2DispatchDate: "",
        notice2AppearanceDate: "",
        notice3DispatchDate: "",
        notice3AppearanceDate: "",
        dpoLetterDispatchDate: "",
        dpoLetterAppearanceDate: "",
      });
      setSelectedGist([]);
      setIsOtherGist(false);
      setOtherGistText("");
      setSelectedRow(null);
      setManualEnquiry(true);
      setEnquiryStatus(null);
      setPersonNoticeStatus(null);
      setComplainantLocked(false);
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setSubmitAttempted(false);
  };

  const handleDownloadSelected = async () => {
    if (!selectedTableRow?.srno) {
      alert("Select a row first.");
      return;
    }
    try {
      await downloadDocx(selectedTableRow.srno);
    } catch (error) {
      console.error("Error downloading docx:", error);
      alert("Error downloading document.");
    }
  };

  const applyQuickRange = (type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (type === "thisWeek") {
      const day = (today.getDay() + 6) % 7; // Monday=0
      const monday = new Date(today);
      monday.setDate(today.getDate() - day);
      setExportDateFrom(formatInputDate(monday));
      setExportDateTo(formatInputDate(today));
      setCurrentPage(1);
      return;
    }
    if (type === "lastMonth") {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      setExportDateFrom(formatInputDate(start));
      setExportDateTo(formatInputDate(end));
      setCurrentPage(1);
      return;
    }
    if (type === "last90") {
      const start = new Date(today);
      start.setDate(today.getDate() - 89);
      setExportDateFrom(formatInputDate(start));
      setExportDateTo(formatInputDate(today));
      setCurrentPage(1);
      return;
    }
    setExportDateFrom("");
    setExportDateTo("");
    setCurrentPage(1);
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      alert("Permission denied");
      return;
    }
    if (!hasOfficerProfile || profileDirty) {
      const profileSaved = await saveOfficerProfile();
      if (!profileSaved) return;
    }
    setSubmitAttempted(true);
    if (hasRequiredErrors) {
      alert("Please fill all required fields.");
      return;
    }

    const url = dialogType === "Add" ? `${API_BASE}/api/summons` : `${API_BASE}/api/summons/${selectedRow.srno}`;
    const method = dialogType === "Add" ? "POST" : "PUT";

    try {
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const savedRow = await response.json();
        handleDialogClose();
        fetchSummonData(currentPage);
        window.dispatchEvent(new CustomEvent("summon-updated"));
        // refresh enquiry list after creating new case
        try {
          const res = await fetch(`${API_BASE}/api/summons/enquiries`);
          if (res.ok) {
            const list = await res.json();
            setEnquiryOptions(Array.isArray(list) ? list : []);
          }
        } catch {}
        alert(`${dialogType} entry successful!`);
        if (savedRow?.srno) {
          try {
            await downloadDocx(savedRow.srno);
          } catch (error) {
            console.error("Docx download failed:", error);
            alert("Entry saved, but DOCX download failed. Check server/template.");
          }
        }
      } else {
        alert(`Failed to ${dialogType.toLowerCase()} entry.`);
      }
    } catch (error) {
      console.error(`Error ${dialogType.toLowerCase()}ing entry:`, error);
      alert(`An error occurred while ${dialogType.toLowerCase()}ing entry.`);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      alert("Permission denied");
      return;
    }
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        const response = await fetch(`${API_BASE}/api/summons/${row.srno}`, { method: "DELETE" });
        if (response.ok) {
          fetchSummonData(currentPage);
          window.dispatchEvent(new CustomEvent("summon-updated"));
          alert("Entry deleted successfully!");
        } else {
          alert("Failed to delete entry.");
        }
      } catch (error) {
        console.error("Error deleting entry:", error);
        alert("An error occurred while deleting entry.");
      }
    }
  };

    // Function to clear all data from the table
    const clearTable = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/summons/clear`, {
          method: "DELETE",
        });
    
        if (response.ok) {
          alert("All summons data cleared successfully.");
          setSummonData([]); // Clear data in the frontend
          window.dispatchEvent(new CustomEvent("summon-updated"));
        } else {
          alert("Failed to clear summons data.");
        }
      } catch (error) {
        console.error("Error clearing table:", error);
        alert("An error occurred while clearing the table.");
      }
    };

    const mapSummonsForExport = (rows) =>
      rows.map((row) => ({
        "Sr No": row.srno,
        "Enquiry Case Number": row.enquirycasenum,
        Priority: row.priority || "",
        Name: row.cname,
        Address: row.address,
        Phone: row.phone,
        CNIC: row.cnic,
        "Notice Number": row.noticenum,
        "Notice Dispatch Date": formatDisplayDate(row.noticedispatchdate),
        "Appearance Date": formatDisplayDate(row.appearencedate),
        "Complainant Name": row.complainantname,
        "Gist of Allegations": row.gistofallegations,
        "Role of Alleged": row.roleofalleged,
        AttendOn: row.attendon,
        "1stNotice": formatDisplayDate(row.notice1dispatchdate),
        "1stNoticeAppearence": formatDisplayDate(row.notice1appearancedate),
        "2ndNotice": formatDisplayDate(row.notice2dispatchdate),
        "2ndNoticeAppearence": formatDisplayDate(row.notice2appearancedate),
        "3rdNotice": formatDisplayDate(row.notice3dispatchdate),
        "3rdNoticeAppearence": formatDisplayDate(row.notice3appearancedate),
        DPOLetterDispatch: formatDisplayDate(row.dpoletterdispatchdate),
        DPOLetterStatus: formatDisplayDate(row.dpoletterappearancedate),
        RoleOfAllegedPerson: row.roleofalleged,
      }));

    const handleExport = async () => {
      setExporting(true);
      setExportProgress(0);
      try {
        if (exportDateFrom && exportDateTo && exportDateFrom > exportDateTo) {
          alert("Export start date must be before end date.");
          return;
        }
        const limit = 200;
        const exportParams = new URLSearchParams();
        exportParams.set("page", "1");
        exportParams.set("limit", String(limit));
        const normalizedSearch = search.trim();
        if (normalizedSearch) exportParams.set("search", normalizedSearch);
        if (noticeStageFilter) exportParams.set("noticeStage", noticeStageFilter);
        if (exportDateFrom) exportParams.set("dateFrom", exportDateFrom);
        if (exportDateTo) exportParams.set("dateTo", exportDateTo);
        if (dateField) exportParams.set("dateField", dateField);
        const firstRes = await fetch(`${API_BASE}/api/summons?${exportParams.toString()}`);
        if (!firstRes.ok) throw new Error("Failed to fetch data");
        const firstJson = await firstRes.json();
        const totalEntries = Number(firstJson.totalEntries || 0);
        const totalPages = Math.max(1, Math.ceil(totalEntries / limit));
        let allRows = firstJson.data || [];
        setExportProgress(Math.round((1 / totalPages) * 100));

        for (let page = 2; page <= totalPages; page += 1) {
          const pageParams = new URLSearchParams();
          pageParams.set("page", String(page));
          pageParams.set("limit", String(limit));
          const normalizedSearch = search.trim();
          if (normalizedSearch) pageParams.set("search", normalizedSearch);
          if (noticeStageFilter) pageParams.set("noticeStage", noticeStageFilter);
          if (exportDateFrom) pageParams.set("dateFrom", exportDateFrom);
          if (exportDateTo) pageParams.set("dateTo", exportDateTo);
          if (dateField) pageParams.set("dateField", dateField);
          const res = await fetch(`${API_BASE}/api/summons?${pageParams.toString()}`);
          if (!res.ok) throw new Error("Failed to fetch data");
          const json = await res.json();
          allRows = allRows.concat(json.data || []);
          setExportProgress(Math.round((page / totalPages) * 100));
        }

        if (allRows.length === 0) {
          alert("No data available for export.");
          return;
        }

        const worksheet = XLSX.utils.json_to_sheet(mapSummonsForExport(allRows));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Summons Data");
        XLSX.writeFile(workbook, "SummonsData.xlsx");
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      } finally {
        setExporting(false);
        setExportProgress(0);
      }
    };

    const handleImport = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!canEdit) {
        alert("Permission denied");
        event.target.value = "";
        return;
      }

      setUploading(true);
      setUploadProgress(0);

      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const errors = [];

        const buildRecords = (row, rowIndex) => {
          const normalized = {};
          Object.entries(row).forEach(([k, v]) => {
            normalized[normalizeKey(k)] = v;
          });
          const get = (...keys) => {
            for (const key of keys) {
              const val = normalized[normalizeKey(key)];
              if (val !== undefined && val !== null && String(val).trim() !== "") return val;
            }
            return "";
          };

          const enquiryCaseNum = get("enquiry/caseno", "enquirycaseno", "enquirycasenum", "enquirycase", "enquirycase#");
          const allegedPeopleRaw = get("allegedpeople", "allegedpersons", "allegedperson", "accused", "alleged");
          const allegedAddressRaw = get("allegedaddress", "allegedaddresses", "address");
          const roleOfAlleged = get("roleofalleged", "role", "roleofallegedperson", "roleofallegedperson");
          const attendOn = get("attendon", "attendondetails", "attendonstatus", "attendondate");
          const complainantName = get("complainantname", "complainant");
          const gistOfAllegations = get("gistofallegations", "gist", "allegations");

          const noticeNum = get("noticenum", "noticenumber", "notice");

          const notice1DispatchRaw = get("1stnotice", "1stnoticedispatchdate", "1stnoticedispatch", "firstnoticedispatch");
          const notice1AppearanceRaw = get("1stnoticeappearence", "1stnoticeappearance", "1stnoticeapperence", "firstnoticeappearance");
          const notice2DispatchRaw = get("2ndnotice", "2ndnoticedispatchdate", "2ndnoticedispatch", "secondnoticedispatch");
          const notice2AppearanceRaw = get("2ndnoticeappearence", "2ndnoticeappearance", "2ndnoticeapperence", "secondnoticeappearance");
          const notice3DispatchRaw = get("3rdnotice", "3rdnoticedispatchdate", "3rdnoticedispatch", "thirdnoticedispatch");
          const notice3AppearanceRaw = get("3rdnoticeappearence", "3rdnoticeappearance", "3rdnoticeapperence", "thirdnoticeappearance");
          const dpoDispatchRaw = get("dpoletterdispatch", "dpoletterdispatchdate");
          const dpoAppearanceRaw = get("dpoletterstatus", "dpoletterappearancedate", "dpoletterappearance", "dpoletterappearence");

          const notice1DispatchParsed = parseDateWithError(notice1DispatchRaw);
          const notice1AppearanceParsed = parseDateWithError(notice1AppearanceRaw);
          const notice2DispatchParsed = parseDateWithError(notice2DispatchRaw);
          const notice2AppearanceParsed = parseDateWithError(notice2AppearanceRaw);
          const notice3DispatchParsed = parseDateWithError(notice3DispatchRaw);
          const notice3AppearanceParsed = parseDateWithError(notice3AppearanceRaw);
          const dpoDispatchParsed = parseDateWithError(dpoDispatchRaw);
          const dpoAppearanceParsed = parseDateWithError(dpoAppearanceRaw);

          const pushError = (column, raw, err) => {
            if (!err) return;
            errors.push({
              row: rowIndex + 2,
              column,
              value: String(raw),
              message: err,
            });
          };
          pushError("1stNotice", notice1DispatchRaw, notice1DispatchParsed.error);
          pushError("1stNoticeAppearence", notice1AppearanceRaw, notice1AppearanceParsed.error);
          pushError("2ndNotice", notice2DispatchRaw, notice2DispatchParsed.error);
          pushError("2ndNoticeAppearence", notice2AppearanceRaw, notice2AppearanceParsed.error);
          pushError("3rdNotice", notice3DispatchRaw, notice3DispatchParsed.error);
          pushError("3rdNoticeAppearence", notice3AppearanceRaw, notice3AppearanceParsed.error);
          pushError("DPOLetterDispatch", dpoDispatchRaw, dpoDispatchParsed.error);
          pushError("DPOLetterStatus", dpoAppearanceRaw, dpoAppearanceParsed.error);

          const notice1DispatchDate = notice1DispatchParsed.iso;
          const notice1AppearanceDate = notice1AppearanceParsed.iso;
          const notice2DispatchDate = notice2DispatchParsed.iso;
          const notice2AppearanceDate = notice2AppearanceParsed.iso;
          const notice3DispatchDate = notice3DispatchParsed.iso;
          const notice3AppearanceDate = notice3AppearanceParsed.iso;
          const dpoLetterDispatchDate = dpoDispatchParsed.iso;
          const dpoLetterAppearanceDate = dpoAppearanceParsed.iso;

          const peopleList = splitSerialList(allegedPeopleRaw);
          const addressList = splitSerialList(allegedAddressRaw);

          const base = {
            enquiryCaseNum,
            phone: get("phone", "phonenumber", "contact"),
            noticeNum,
            complainantName,
            gistOfAllegations,
            roleOfAlleged,
            attendOn,
            notice1DispatchDate,
            notice1AppearanceDate,
            notice2DispatchDate,
            notice2AppearanceDate,
            notice3DispatchDate,
            notice3AppearanceDate,
            dpoLetterDispatchDate,
            dpoLetterAppearanceDate,
          };

          if (!peopleList.length) {
            const name = get("name", "fullname", "allegedname");
            const cnic = get("cnic", "nic");
            const phone = get("phone", "phonenumber", "contact");
            const address = addressList[0] || get("address");
            const resolvedNotice =
              noticeNum || computeNextNotice({
                notice1DispatchDate,
                notice2DispatchDate,
                notice3DispatchDate,
                dpoLetterDispatchDate,
              });
            return [
              {
                ...base,
                noticeNum: resolvedNotice,
                name,
                cnic,
                phone,
                address,
              },
            ];
          }

          return peopleList.map((person, index) => {
            const extracted = extractNameCnic(person);
            const address = addressList[index] || (addressList.length === 1 ? addressList[0] : "");
            const resolvedNotice =
              noticeNum || computeNextNotice({
                notice1DispatchDate,
                notice2DispatchDate,
                notice3DispatchDate,
                dpoLetterDispatchDate,
              });
            return {
              ...base,
              noticeNum: resolvedNotice,
              name: extracted.name,
              cnic: extracted.cnic,
              phone: extracted.phone || base.phone,
              address,
            };
          });
        };

        const records = rawRows.flatMap((row, idx) => buildRecords(row, idx)).filter((r) => r.name || r.cnic || r.address);
        if (errors.length) {
          setImportErrors(errors.slice(0, 50));
          setImportErrorOpen(true);
          return;
        }
        if (!records.length) {
          alert("No valid rows found in the file.");
          return;
        }

        const chunkSize = 200;
        for (let i = 0; i < records.length; i += chunkSize) {
          const chunk = records.slice(i, i + chunkSize);
          const res = await fetch(`${API_BASE}/api/summons/bulk-upload`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ data: chunk }),
          });
          if (!res.ok) throw new Error("Upload failed");
          const percent = Math.round(((i + chunk.length) / records.length) * 100);
          setUploadProgress(percent);
        }

        alert(`Upload complete. Imported ${records.length} rows.`);
        fetchSummonData(currentPage);
        window.dispatchEvent(new CustomEvent("summon-updated"));
      } catch (error) {
        console.error("Import failed:", error);
        alert("Import failed. Please check the file format.");
      } finally {
        setUploading(false);
        setUploadProgress(0);
        event.target.value = "";
      }
    };
  
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const visibleSummonColumns = SUMMON_COLUMNS.filter(
    (col) => summonColumnVisibility[col.id] !== false
  );
  const firstVisibleSummonColumn = visibleSummonColumns[0]?.id;
  const mobileSummonColumns = visibleSummonColumns.filter(
    (col) => col.id !== "srno" && col.id !== "actions"
  );

  const getSummonCellSx = (colId, { isHeader = false, isSelected = false } = {}) => {
    const def = SUMMON_COLUMN_MAP[colId] || {};
    const width = summonColumnWidths[colId] || def.minWidth || 120;
    const sx = {
      minWidth: width,
      width,
      whiteSpace: def.wrap ? "normal" : "nowrap",
    };
    if (colId === firstVisibleSummonColumn) {
      sx.position = "sticky";
      sx.left = 0;
      sx.zIndex = isHeader ? 3 : 1;
      sx.backgroundColor = isHeader
        ? "background.paper"
        : isSelected
          ? "action.selected"
          : "background.paper";
    }
    return sx;
  };

  const handleSummonVisibilityToggle = (id) => (event) => {
    const checked = event.target.checked;
    setSummonColumnVisibility((prev) => {
      const next = { ...prev, [id]: checked };
      const visibleCount = Object.values(next).filter(Boolean).length;
      return visibleCount === 0 ? prev : next;
    });
  };

  const handleSummonWidthChange = (id) => (event) => {
    const def = SUMMON_COLUMN_MAP[id] || {};
    const fallback = def.minWidth || 120;
    const parsed = Number(event.target.value);
    const width = Number.isFinite(parsed) && parsed >= 60 ? parsed : fallback;
    setSummonColumnWidths((prev) => ({ ...prev, [id]: width }));
  };

  const resetSummonColumns = () => {
    setSummonColumnVisibility(buildSummonVisibility());
    setSummonColumnWidths(buildColumnWidths(SUMMON_COLUMNS));
  };

  const openHistoryDialog = (row) => {
    setHistoryRow(row);
    setHistoryOpen(true);
  };

  const renderSummonCell = (colId, row) => {
    switch (colId) {
      case "srno":
        return row.srno;
      case "enquirycasenum":
        return row.enquirycasenum;
      case "priority":
        return row.priority ? (
          <Chip size="small" label={row.priority} color={getPriorityChipColor(row.priority)} />
        ) : (
          "—"
        );
      case "cname":
        return row.cname;
      case "address":
        return row.address;
      case "phone":
        return row.phone;
      case "cnic":
        return row.cnic;
      case "complainantname":
        return row.complainantname;
      case "gistofallegations":
        return row.gistofallegations;
      case "roleofalleged":
        return row.roleofalleged;
      case "attendon":
        return row.attendon;
      case "noticeprogress": {
        const steps = [
          { key: "1st", done: !!row.notice1dispatchdate },
          { key: "2nd", done: !!row.notice2dispatchdate },
          { key: "3rd", done: !!row.notice3dispatchdate },
          { key: "DPO", done: !!row.dpoletterdispatchdate },
        ];
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {steps.map((step) => (
              <Chip
                key={step.key}
                size="small"
                label={step.key}
                color={step.done ? "success" : "default"}
                variant={step.done ? "filled" : "outlined"}
                onClick={(event) => {
                  event.stopPropagation();
                  openHistoryDialog(row);
                }}
              />
            ))}
          </Stack>
        );
      }
      case "notice1dispatchdate":
        return formatDisplayDate(row.notice1dispatchdate);
      case "notice1appearancedate":
        return formatDisplayDate(row.notice1appearancedate);
      case "notice2dispatchdate":
        return formatDisplayDate(row.notice2dispatchdate);
      case "notice2appearancedate":
        return formatDisplayDate(row.notice2appearancedate);
      case "notice3dispatchdate":
        return formatDisplayDate(row.notice3dispatchdate);
      case "notice3appearancedate":
        return formatDisplayDate(row.notice3appearancedate);
      case "dpoletterdispatchdate":
        return formatDisplayDate(row.dpoletterdispatchdate);
      case "dpoletterappearancedate":
        return formatDisplayDate(row.dpoletterappearancedate);
      case "actions":
        return (
          <Stack direction="row" spacing={1}>
            <Button
              onClick={(event) => {
                event.stopPropagation();
                handleOpenDialog("Edit", row);
              }}
              disabled={!canEdit}
            >
              Edit
            </Button>
            <Button
              color="secondary"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(row);
              }}
              disabled={!canDelete}
            >
              Delete
            </Button>
          </Stack>
        );
      default:
        return "";
    }
  };

  const noticeSelectionStatus = personNoticeStatus || enquiryStatus;
  const noticeLockStatus = personNoticeStatus;
  const noticeLocks = {
    notice1: dialogType === "Add" && noticeLockStatus?.hasNotice1,
    notice2: dialogType === "Add" && noticeLockStatus?.hasNotice2,
    notice3: dialogType === "Add" && noticeLockStatus?.hasNotice3,
    dpo: dialogType === "Add" && noticeLockStatus?.hasDpo,
  };

  const showNotice1Fields = formData.noticeNum === "1st Notice";
  const showNotice2Fields = formData.noticeNum === "2nd Notice";
  const showNotice3Fields = formData.noticeNum === "3rd Notice";
  const showDpoFields = formData.noticeNum === "DPO Letter";

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 }, width: "100%", minWidth: 0 }}
    >

<Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" sx={{ width: "100%" }}>
<Button variant="contained" color="primary" onClick={() => handleOpenDialog("Add")} disabled={!canEdit}>
            Add New Entry
          </Button>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDownloadSelected}
              disabled={!selectedTableRow}
            >
              Download DOCX
            </Button>
          {/* <Button variant="contained" color="secondary" onClick={clearTable}>
              Clear Table
            </Button> */}
            <Button variant="outlined" onClick={() => setSummonColumnsOpen(true)}>
              Columns
            </Button>
            <Button variant="contained" color="primary" onClick={handleExport} disabled={exporting}>
              Export All
            </Button>
            <Button variant="contained" color="secondary" component="label" disabled={uploading || !canEdit}>
              Upload Data
              <input type="file" hidden onChange={handleImport} />
            </Button>
          </Stack>
        </Stack>

        {(exporting || uploading) && (
          <Box sx={{ mt: 2 }}>
            {exporting && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">Exporting... {exportProgress}%</Typography>
                <LinearProgress variant="determinate" value={exportProgress} />
              </Box>
            )}
            {uploading && (
              <Box>
                <Typography variant="body2">Uploading... {uploadProgress}%</Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            sx={{ width: { xs: "100%", md: 180 } }}
          />
          <FormControl sx={{ minWidth: { xs: "100%", md: 220 } }}>
            <InputLabel>Notice Stage</InputLabel>
            <Select
              label="Notice Stage"
              value={noticeStageFilter}
              onChange={(e) => {
                setNoticeStageFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="None">None</MenuItem>
              <MenuItem value="1st Notice">1st Notice</MenuItem>
              <MenuItem value="2nd Notice">2nd Notice</MenuItem>
              <MenuItem value="3rd Notice">3rd Notice</MenuItem>
              <MenuItem value="DPO Letter">DPO Letter</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: { xs: "100%", md: 200 } }}>
            <InputLabel>Date Type</InputLabel>
            <Select
              label="Date Type"
              value={dateField}
              onChange={(e) => {
                setDateField(e.target.value);
                setCurrentPage(1);
              }}
            >
              <MenuItem value="dispatch">Dispatch Date</MenuItem>
              <MenuItem value="appearance">Appearance Date</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Date From"
            type="date"
            value={exportDateFrom}
            onChange={(e) => {
              setExportDateFrom(e.target.value);
              setCurrentPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: "100%", md: 180 } }}
          />
          <TextField
            label="Date To"
            type="date"
            value={exportDateTo}
            onChange={(e) => {
              setExportDateTo(e.target.value);
              setCurrentPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: "100%", md: 180 } }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={() => applyQuickRange("thisWeek")}>
              This Week
            </Button>
            <Button variant="outlined" onClick={() => applyQuickRange("lastMonth")}>
              Last Month
            </Button>
            <Button variant="outlined" onClick={() => applyQuickRange("last90")}>
              Last 90 Days
            </Button>
            <Button variant="text" onClick={() => applyQuickRange("clear")}>
              Clear
            </Button>
          </Stack>
        </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ width: "100%", minWidth: 0 }}
        alignItems="stretch"
      >
        <Box flex={1} sx={{ width: "100%", minWidth: 0 }}>
          {isMobile ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {summonData.length > 0 ? (
                summonData.map((row) => {
                  const isSelected = selectedTableRow?.srno === row.srno;
                  return (
                    <Card
                      key={row.srno}
                      variant="outlined"
                      onClick={() => setSelectedTableRow(row)}
                      onDoubleClick={() => canEdit && handleOpenDialog("Edit", row)}
                      sx={{
                        cursor: "pointer",
                        borderColor: isSelected ? "primary.main" : "divider",
                        bgcolor: isSelected ? "action.selected" : "background.paper",
                      }}
                    >
                      <CardHeader
                        title={row.cname || "Unnamed"}
                        subheader={`Enquiry: ${row.enquirycasenum || "—"}`}
                        action={
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Chip
                              size="small"
                              label={row.noticenum || "Notice"}
                              color={getNoticeChipColor(row.noticenum)}
                            />
                            {renderSummonCell("noticeprogress", row)}
                          </Stack>
                        }
                      />
                      <Divider />
                      <CardContent>
                        <Stack spacing={1.5}>
                          {mobileSummonColumns.map((col) => {
                            const value = renderSummonCell(col.id, row);
                            return (
                              <Box key={col.id}>
                                <Typography variant="caption" color="text.secondary">
                                  {col.label}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                  {value === "" || value === null || value === undefined ? "—" : value}
                                </Typography>
                              </Box>
                            );
                          })}
                          <Divider />
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenDialog("Edit", row);
                              }}
                              disabled={!canEdit}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="secondary"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(row);
                              }}
                              disabled={!canDelete}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">No data available</Typography>
                </Paper>
              )}
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: { xs: 360, sm: 440, md: 520 },
              }}
            >
              <Table stickyHeader sx={{ width: "100%", minWidth: 1400 }}>
                <TableHead>
                  <TableRow>
                    {visibleSummonColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={getSummonCellSx(col.id, { isHeader: true })}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
              {summonData.length > 0 ? (
                summonData.map((row) => {
                  const isSelected = selectedTableRow?.srno === row.srno;
                  return (
                        <TableRow
                          key={row.srno}
                          hover
                          selected={isSelected}
                          onClick={() => setSelectedTableRow(row)}
                          onDoubleClick={() => canEdit && handleOpenDialog("Edit", row)}
                          sx={{ cursor: "pointer" }}
                        >
                          {visibleSummonColumns.map((col) => (
                            <TableCell
                              key={col.id}
                              sx={getSummonCellSx(col.id, { isSelected })}
                            >
                              {renderSummonCell(col.id, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={Math.max(visibleSummonColumns.length, 1)}>
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{ mt: 3, display: "flex", justifyContent: "center", width: "100%" }}
          />
        </Box>

        <Dialog
          open={summonColumnsOpen}
          onClose={() => setSummonColumnsOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Summon Table Columns</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {SUMMON_COLUMNS.map((col) => (
                <Stack
                  key={col.id}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={summonColumnVisibility[col.id] !== false}
                        onChange={handleSummonVisibilityToggle(col.id)}
                      />
                    }
                    label={col.label}
                  />
                  <TextField
                    label="Width"
                    size="small"
                    type="number"
                    value={summonColumnWidths[col.id] || col.minWidth || 120}
                    onChange={handleSummonWidthChange(col.id)}
                    inputProps={{ min: 60, max: 600 }}
                    sx={{ width: 140 }}
                  />
                </Stack>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetSummonColumns}>Reset</Button>
            <Button variant="contained" onClick={() => setSummonColumnsOpen(false)}>
              Done
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Notice History</DialogTitle>
          <DialogContent dividers>
            {historyRow ? (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  {historyRow.cname || "Unnamed"} — {historyRow.enquirycasenum || "—"}
                </Typography>
                <Divider />
                <Stack spacing={1}>
                  {[
                    {
                      label: "1st Notice",
                      date: historyRow.notice1dispatchdate,
                      appearance: historyRow.notice1appearancedate,
                    },
                    {
                      label: "2nd Notice",
                      date: historyRow.notice2dispatchdate,
                      appearance: historyRow.notice2appearancedate,
                    },
                    {
                      label: "3rd Notice",
                      date: historyRow.notice3dispatchdate,
                      appearance: historyRow.notice3appearancedate,
                    },
                    {
                      label: "DPO Letter",
                      date: historyRow.dpoletterdispatchdate,
                      appearance: historyRow.dpoletterappearancedate,
                    },
                  ].map((item, idx, arr) => (
                    <Stack key={item.label} direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          position: "relative",
                          width: 16,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: item.date ? "success.main" : "divider",
                            mt: 0.5,
                          }}
                        />
                        {idx < arr.length - 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 14,
                              bottom: -14,
                              width: 2,
                              bgcolor: "divider",
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">{item.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Dispatch: {formatDisplayDate(item.date) || "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Appearance: {formatDisplayDate(item.appearance) || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2">No history available.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryOpen(false)} color="secondary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Add/Edit */}
        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>{dialogType === "Add" ? "Add New Entry" : "Edit Entry"}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Fields marked with * are required.
            </Typography>
            <Stack spacing={2}>
              {!hasOfficerProfile && (
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Complete Officer Profile
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Investigation Officer Name"
                      name="investigationOfficerName"
                      value={profileData.investigationOfficerName}
                      onChange={handleProfileFieldChange}
                      fullWidth
                      required
                    />
                    <FormControl fullWidth required>
                      <InputLabel>IO Designation</InputLabel>
                      <Select
                        label="IO Designation"
                        value={getIoDesignationCode(profileData.ioDesignation)}
                        onChange={(event) => {
                          const option = IO_DESIGNATION_OPTIONS.find(
                            (item) => item.code === event.target.value
                          );
                          setProfileData((prev) => ({
                            ...prev,
                            ioDesignation: option?.value || "",
                          }));
                        }}
                      >
                        <MenuItem value="">Select</MenuItem>
                        {IO_DESIGNATION_OPTIONS.map((option) => (
                          <MenuItem key={option.code} value={option.code}>
                            {option.code}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={saveOfficerProfile}
                      disabled={profileSaving}
                    >
                      {profileSaving ? "Saving..." : "Save Profile"}
                    </Button>
                  </Stack>
                </Box>
              )}
              <TextField
                label="Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
                error={submitAttempted && requiredErrors.name}
                helperText={submitAttempted && requiredErrors.name ? "Required" : ""}
              />
              <TextField
                label="Address *"
                name="address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                required
                error={submitAttempted && requiredErrors.address}
                helperText={submitAttempted && requiredErrors.address ? "Required" : ""}
              />
              <TextField label="Phone" name="phone" value={formData.phone} onChange={handleChange} fullWidth />
              <TextField label="CNIC" name="cnic" value={formData.cnic} onChange={handleChange} fullWidth />

              {/* Dropdown for Notice Number */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <FormControl fullWidth required error={submitAttempted && requiredErrors.noticeNum}>
                  <InputLabel>Notice Number *</InputLabel>
                  <Select
                    label="Notice Number *"
                    name="noticeNum"
                    value={formData.noticeNum}
                    onChange={handleChange}
                  >
                    <MenuItem value="1st Notice" disabled={!!noticeSelectionStatus?.hasNotice1}>
                      1st Notice
                    </MenuItem>
                    <MenuItem value="2nd Notice" disabled={!!noticeSelectionStatus?.hasNotice2}>
                      2nd Notice
                    </MenuItem>
                    <MenuItem value="3rd Notice" disabled={!!noticeSelectionStatus?.hasNotice3}>
                      3rd Notice
                    </MenuItem>
                    <MenuItem value="DPO Letter" disabled={!!noticeSelectionStatus?.hasDpo}>
                      DPO Letter
                    </MenuItem>
                  </Select>
                  {submitAttempted && requiredErrors.noticeNum && (
                    <FormHelperText>Required</FormHelperText>
                  )}
                </FormControl>
                <Tooltip title={noticeHistoryTooltip} placement="top">
                  <Chip
                    label={formData.noticeNum || "Notice"}
                    size="small"
                    color={getNoticeChipColor(formData.noticeNum)}
                    variant="outlined"
                    sx={{ minWidth: 92 }}
                  />
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">Enquiry Case Number *</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={manualEnquiry}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (!checked && enquiryOptions.length === 0) {
                          alert("No enquiry list available. Manual entry will remain enabled.");
                          return;
                        }
                        setManualEnquiry(checked);
                      }}
                    />
                  }
                  label="Manual entry"
                />
              </Stack>
              {manualEnquiry ? (
                <TextField
                  label="Enquiry Case Number *"
                  name="enquiryCaseNum"
                  value={formData.enquiryCaseNum}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={submitAttempted && requiredErrors.enquiryCaseNum}
                  helperText={submitAttempted && requiredErrors.enquiryCaseNum ? "Required" : ""}
                />
              ) : (
                <FormControl fullWidth required error={submitAttempted && requiredErrors.enquiryCaseNum}>
                  <InputLabel>Enquiry Case Number *</InputLabel>
                  <Select
                    label="Enquiry Case Number *"
                    name="enquiryCaseNum"
                    value={formData.enquiryCaseNum}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      {enquiryLoading ? "Loading..." : "Select enquiry"}
                    </MenuItem>
                    {enquiryOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {submitAttempted && requiredErrors.enquiryCaseNum && (
                    <FormHelperText>Required</FormHelperText>
                  )}
                </FormControl>
              )}
              <TextField
                label="Complainant Name *"
                name="complainantName"
                value={formData.complainantName}
                onChange={handleChange}
                fullWidth
                required
                error={submitAttempted && requiredErrors.complainantName}
                helperText={submitAttempted && requiredErrors.complainantName ? "Required" : ""}
                disabled={complainantLocked}
              />
              <TextField
                label="Role of Alleged (Saved Only)"
                name="roleOfAlleged"
                value={formData.roleOfAlleged}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="AttendOn / Status"
                name="attendOn"
                value={formData.attendOn}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
              />
              {showNotice1Fields && (
                <>
                  <TextField
                    label="1st Notice Dispatch Date"
                    name="notice1DispatchDate"
                    type="date"
                    value={formData.notice1DispatchDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.notice1}
                  />
                  <TextField
                    label="1st Notice Appearance Date"
                    name="notice1AppearanceDate"
                    type="date"
                    value={formData.notice1AppearanceDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.notice1}
                  />
                </>
              )}
              {showNotice2Fields && (
                <>
                  <TextField
                    label="2nd Notice Dispatch Date"
                    name="notice2DispatchDate"
                    type="date"
                    value={formData.notice2DispatchDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.notice2}
                  />
                  <TextField
                    label="2nd Notice Appearance Date"
                    name="notice2AppearanceDate"
                    type="date"
                    value={formData.notice2AppearanceDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.notice2}
                  />
                </>
              )}
              {showNotice3Fields && (
                <>
                  <TextField
                    label="3rd Notice Dispatch Date"
                    name="notice3DispatchDate"
                    type="date"
                    value={formData.notice3DispatchDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.notice3}
                  />
                  <TextField
                    label="3rd Notice Appearance Date"
                    name="notice3AppearanceDate"
                    type="date"
                    value={formData.notice3AppearanceDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.notice3}
                  />
                </>
              )}
              {showDpoFields && (
                <>
                  <TextField
                    label="DPO Letter Dispatch Date"
                    name="dpoLetterDispatchDate"
                    type="date"
                    value={formData.dpoLetterDispatchDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.dpo}
                  />
                  <TextField
                    label="DPO Letter Appearance Date"
                    name="dpoLetterAppearanceDate"
                    type="date"
                    value={formData.dpoLetterAppearanceDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled={noticeLocks.dpo}
                  />
                </>
              )}

              {/* Checkbox group for Gist of Allegations */}
              <FormGroup>
                <InputLabel>Gist of Allegations *</InputLabel>
                {gistOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={selectedGist.includes(option)}
                        onChange={handleGistChange}
                        name={option}
                        disabled={isOtherGist}
                      />
                    }
                    label={option}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isOtherGist}
                      onChange={handleOtherGistToggle}
                      name="otherGist"
                    />
                  }
                  label="Other (type your own)"
                />
                {isOtherGist && (
                  <TextField
                    label={`Other Gist (max 60 words) *`}
                    value={otherGistText}
                    onChange={handleOtherGistChange}
                    fullWidth
                    multiline
                    minRows={2}
                    error={submitAttempted && requiredErrors.gistOfAllegations}
                    helperText={
                      submitAttempted && requiredErrors.gistOfAllegations
                        ? "Required"
                        : `${otherWordsRemaining} words remaining`
                    }
                  />
                )}
                {!isOtherGist && submitAttempted && requiredErrors.gistOfAllegations && (
                  <FormHelperText error>Required</FormHelperText>
                )}
              </FormGroup>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">Cancel</Button>
            <Button onClick={handleSubmit} color="primary">{dialogType}</Button>
          </DialogActions>
        </Dialog>
      </Stack>

      <Dialog open={importErrorOpen} onClose={() => setImportErrorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invalid Date Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Fix the dates in the Excel file and try again. Supported formats: DD/MM/YYY or MM/DD/YYY.
          </Typography>
          {importErrors.map((err, idx) => (
            <Typography key={`${err.row}-${idx}`} variant="body2">
              Row {err.row}, {err.column}: "{err.value}" — {err.message}
            </Typography>
          ))}
          {importErrors.length >= 50 && (
            <Typography variant="caption">Showing first 50 errors.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportErrorOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SummonNotice;
